import React, { useState } from 'react';
import { Trash2, Edit, FileText } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const EnergiaTartaly = () => {
  const [inputText, setInputText] = useState('');
  const [selectedType, setSelectedType] = useState('green');
  const [energyItems, setEnergyItems] = useState([]);
  const [drainItems, setDrainItems] = useState([]);
  const [showRating, setShowRating] = useState(false);
  const [currentItem, setCurrentItem] = useState('');
  const [currentRating, setCurrentRating] = useState(5);
  const [calculated, setCalculated] = useState(false);
  const [totalEnergy, setTotalEnergy] = useState(0);
  const [totalDrain, setTotalDrain] = useState(0);
  const [editingItem, setEditingItem] = useState(null);
  const [editText, setEditText] = useState('');

  const accentColor = '#01918C';
  const lightAccentColor = '#02c7c0';

  const addItem = () => {
    if (inputText.trim()) {
      setCurrentItem(inputText);
      setShowRating(true);
      setInputText('');
    }
  };

  const submitRating = () => {
    const newItem = {
      text: currentItem,
      rating: currentRating,
      id: Date.now()
    };

    if (selectedType === 'green') {
      setEnergyItems([...energyItems, newItem]);
    } else {
      setDrainItems([...drainItems, newItem]);
    }

    setShowRating(false);
    setCurrentItem('');
    setCurrentRating(5);
    setCalculated(false);
  };

  const calculate = () => {
    const energySum = energyItems.reduce((sum, item) => sum + item.rating, 0);
    const drainSum = drainItems.reduce((sum, item) => sum + item.rating, 0);
    setTotalEnergy(energySum);
    setTotalDrain(drainSum);
    setCalculated(true);
  };

  const deleteItem = (id, type) => {
    if (type === 'green') {
      setEnergyItems(energyItems.filter(item => item.id !== id));
    } else {
      setDrainItems(drainItems.filter(item => item.id !== id));
    }
    setCalculated(false);
  };

  const startEdit = (item, type) => {
    setEditingItem({ ...item, type });
    setEditText(item.text);
  };

  const saveEdit = () => {
    if (editingItem.type === 'green') {
      setEnergyItems(energyItems.map(item => 
        item.id === editingItem.id 
          ? { ...item, text: editText }
          : item
      ));
    } else {
      setDrainItems(drainItems.map(item => 
        item.id === editingItem.id 
          ? { ...item, text: editText }
          : item
      ));
    }
    setEditingItem(null);
    setEditText('');
    setCalculated(false);
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setEditText('');
  };

  const exportToPDF = async () => {
    const element = document.getElementById('energia-tartaly-content');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Kisebb margók - 5mm mindenhol
      const margin = 5;
      const availableWidth = pdfWidth - (2 * margin);
      const availableHeight = pdfHeight - (2 * margin);
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
      
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      
      const imgX = (pdfWidth - scaledWidth) / 2;
      const imgY = margin;
      
      pdf.addImage(imgData, 'PNG', imgX, imgY, scaledWidth, scaledHeight);
      
      // Ha túl nagy a tartalom, új oldal
      if (scaledHeight > availableHeight) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', imgX, margin, scaledWidth, scaledHeight);
      }
      
      const date = new Date().toLocaleDateString('hu-HU');
      pdf.save(`energia-tartaly-${date}.pdf`);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Hiba történt a PDF exportálás közben!');
    }
  };

  const formatItemText = (text) => {
    if (text.length <= 20) return text;
    const words = text.split(' ');
    let line = '';
    let result = [];
    
    for (let word of words) {
      if ((line + word).length > 20 && line.length > 0) {
        result.push(line.trim());
        line = word + ' ';
      } else {
        line += word + ' ';
      }
    }
    if (line.trim()) result.push(line.trim());
    
    return result;
  };

  return (
    <div id="energia-tartaly-content" className="w-full flex flex-col items-center p-4 min-h-screen" style={{backgroundColor: '#f8f9fa'}}>
      {/* Tető SVG konténer */}
      <div className="w-full h-[80px] mb-6">
        <svg 
          className="max-w-[1160px] mx-auto block" 
          viewBox="0 0 800 80" 
          style={{stroke: '#212529', strokeWidth: 2}}
        >
          <path 
            d="M50 70 Q400 10 750 70" 
            fill="none" 
            strokeLinecap="round"
          />
          <circle cx="400" cy="40" r="8" fill="#01918C" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto w-full">
        <h1 className="text-4xl font-bold text-center mb-8" style={{color: '#212529'}}>
          ⚡ Energia Tartály ⚡
        </h1>

        {/* Új elem hozzáadása */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4 max-w-4xl mx-auto">
          <h3 className="text-xl font-semibold mb-4" style={{color: '#212529'}}>Új elem hozzáadása</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Írj be egy energia forrást vagy lemeritőt..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-gray-800"
                style={{focusRingColor: accentColor}}
                onKeyPress={(e) => e.key === 'Enter' && addItem()}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedType('green')}
                className={`px-4 py-1.5 rounded-lg font-medium transition-colors ${
                  selectedType === 'green'
                    ? 'text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
                style={selectedType === 'green' ? {backgroundColor: accentColor} : {}}
              >
                🔋 Energizáló
              </button>
              <button
                onClick={() => setSelectedType('red')}
                className={`px-4 py-1.5 rounded-lg font-medium transition-colors ${
                  selectedType === 'red'
                    ? 'text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
                style={selectedType === 'red' ? {backgroundColor: '#dc3545'} : {}}
              >
                🪫 Energia faló
              </button>
            </div>
            <button
              onClick={addItem}
              disabled={!inputText.trim()}
              className="px-6 py-1.5 text-white rounded-lg font-medium hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-all duration-300 shadow-md"
              style={{backgroundColor: accentColor}}
            >
              Hozzáad
            </button>
          </div>
        </div>

        {/* Tartály vizualizáció */}
        <div className="w-full flex justify-center mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h2 className="text-center text-xl font-bold mb-6" style={{color: '#212529'}}>
              Energia Tartály
            </h2>
            
            {/* 3D Tartály - Álló formátum */}
            <div className="relative w-64 h-96 mx-auto mb-6">
              <svg viewBox="0 0 200 320" className="w-full h-full">
                {/* Tartály test - 3D hatás */}
                <defs>
                  <radialGradient id="tankGradient" cx="30%" cy="30%" r="70%">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="20%" stopColor="#f8f8f8" />
                    <stop offset="50%" stopColor="#e8e8e8" />
                    <stop offset="80%" stopColor="#d0d0d0" />
                    <stop offset="100%" stopColor="#b8b8b8" />
                  </radialGradient>
                  <linearGradient id="shadowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#999999" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#666666" stopOpacity="0.8" />
                  </linearGradient>
                  <linearGradient id="greenFluid" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={lightAccentColor} stopOpacity="0.8" />
                    <stop offset="100%" stopColor={accentColor} stopOpacity="0.9" />
                  </linearGradient>
                  <linearGradient id="redFluid" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff8a8a" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#ff6b6b" stopOpacity="0.9" />
                  </linearGradient>
                  <linearGradient id="metalGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#888" />
                    <stop offset="50%" stopColor="#555" />
                    <stop offset="100%" stopColor="#333" />
                  </linearGradient>
                  <filter id="wave" x="0%" y="0%" width="100%" height="100%">
                    <feTurbulence baseFrequency="0.02" numOctaves="3" result="noise" seed="1" />
                    <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" />
                  </filter>
                </defs>
                
                {/* Árnyék háttérben */}
                <ellipse cx="105" cy="285" rx="75" ry="25" fill="url(#shadowGradient)" opacity="0.4" />
                <rect x="35" y="40" width="140" height="240" fill="url(#shadowGradient)" opacity="0.2" />
                
                {/* Tartály teteje - kupoladóm */}
                <ellipse cx="100" cy="35" rx="70" ry="25" fill="url(#tankGradient)" stroke="#888888" strokeWidth="2" />
                
                {/* Tartály test - henger */}
                <rect x="30" y="35" width="140" height="240" fill="url(#tankGradient)" stroke="#888888" strokeWidth="2" />
                
                {/* Tartály alja - csak elülső ív */}
                <path d="M30 275 Q100 310 170 275" fill="url(#tankGradient)" stroke="#888888" strokeWidth="2" />
                
                {/* Felső sérelem/nyomásmérő */}
                <rect x="85" y="15" width="30" height="15" rx="3" fill="url(#metalGradient)" stroke="#666" strokeWidth="1" />
                <circle cx="100" cy="7" r="4" fill="#888" stroke="#555" strokeWidth="1" />
                
                
                {/* Segédvonalak - szintjelölők */}
                <line x1="25" y1="75" x2="35" y2="75" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="25" y1="115" x2="35" y2="115" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="25" y1="155" x2="35" y2="155" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="25" y1="195" x2="35" y2="195" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="25" y1="235" x2="35" y2="235" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                
                <line x1="165" y1="75" x2="175" y2="75" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="165" y1="115" x2="175" y2="115" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="165" y1="155" x2="175" y2="155" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="165" y1="195" x2="175" y2="195" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                <line x1="165" y1="235" x2="175" y2="235" stroke="#cccccc" strokeWidth="1" opacity="0.6" />
                
                {calculated && (
                  <>
                    {/* Zöld folyadék (jobb oldal) */}
                    <path 
                      d={`M100 ${275 - (totalEnergy / Math.max(totalEnergy + totalDrain, 1)) * 240} 
                          L170 ${275 - (totalEnergy / Math.max(totalEnergy + totalDrain, 1)) * 240} 
                          L170 275 
                          Q100 290 100 275 Z`}
                      fill="url(#greenFluid)"
                      filter="url(#wave)"
                    />
                    
                    {/* Piros folyadék (bal oldal) */}
                    <path 
                      d={`M30 ${275 - (totalDrain / Math.max(totalEnergy + totalDrain, 1)) * 240} 
                          L100 ${275 - (totalDrain / Math.max(totalEnergy + totalDrain, 1)) * 240} 
                          L100 275 
                          Q30 290 30 275 Z`}
                      fill="url(#redFluid)"
                      filter="url(#wave)"
                    />
                    
                    {/* Hullámzó felület - zöld */}
                    <path 
                      d={`M100 ${275 - (totalEnergy / Math.max(totalEnergy + totalDrain, 1)) * 240} Q130 ${275 - (totalEnergy / Math.max(totalEnergy + totalDrain, 1)) * 240 - 3} 170 ${275 - (totalEnergy / Math.max(totalEnergy + totalDrain, 1)) * 240}`}
                      fill="none" stroke={lightAccentColor} strokeWidth="2" opacity="0.9"
                    />
                    
                    {/* Hullámzó felület - piros */}
                    <path 
                      d={`M30 ${275 - (totalDrain / Math.max(totalEnergy + totalDrain, 1)) * 240} Q65 ${275 - (totalDrain / Math.max(totalEnergy + totalDrain, 1)) * 240 - 4} 100 ${275 - (totalDrain / Math.max(totalEnergy + totalDrain, 1)) * 240}`}
                      fill="none" stroke="#ff8a8a" strokeWidth="2" opacity="0.9"
                    />
                  </>
                )}
                
                {/* Tartály konturn - első réteg */}
                <ellipse cx="100" cy="35" rx="70" ry="25" fill="none" stroke="#777777" strokeWidth="2" />
                <rect x="30" y="35" width="140" height="240" fill="none" stroke="#777777" strokeWidth="2" />
                <path d="M30 275 Q100 310 170 275" fill="none" stroke="#777777" strokeWidth="2" />
                
                {/* Belső árnyék vonalak */}
                <ellipse cx="100" cy="40" rx="65" ry="20" fill="none" stroke="#999999" strokeWidth="1" opacity="0.5" />
                <rect x="35" y="40" width="130" height="230" fill="none" stroke="#999999" strokeWidth="1" opacity="0.3" />
                
                {/* Fehér fény reflection */}
                <ellipse cx="115" cy="45" rx="12" ry="6" fill="white" opacity="0.5" />
                <ellipse cx="120" cy="80" rx="6" ry="15" fill="white" opacity="0.3" />
                <ellipse cx="125" cy="120" rx="4" ry="25" fill="white" opacity="0.2" />
                
                {/* Lábak - körívhez igazítva */}
                <rect x="45" y="295" width="8" height="20" fill="url(#metalGradient)" stroke="#555" strokeWidth="1" />
                <rect x="70" y="300" width="8" height="15" fill="url(#metalGradient)" stroke="#555" strokeWidth="1" />
                <rect x="120" y="300" width="8" height="15" fill="url(#metalGradient)" stroke="#555" strokeWidth="1" />
                <rect x="145" y="295" width="8" height="20" fill="url(#metalGradient)" stroke="#555" strokeWidth="1" />
              </svg>
            </div>
            
            {/* Címkék */}
            <div className="flex justify-around text-center mb-4">
              <div className="text-sm font-semibold" style={{color: '#dc3545'}}>
                🪫 Energia faló
              </div>
              <div className="text-sm font-semibold" style={{color: accentColor}}>
                🔋 Energizáló
              </div>
            </div>

            {/* Elemek listája két oszlopban */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Energia faló elemek */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  {drainItems.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-2 rounded border" style={{backgroundColor: '#ff6b6b', color: 'white'}}>
                      {editingItem && editingItem.id === item.id ? (
                        <div className="flex-1 flex gap-2 items-center">
                          <input
                            type="text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="flex-1 px-2 py-1 rounded text-gray-800 text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                          />
                          <button onClick={saveEdit} className="px-2 py-1 rounded text-xs font-bold" style={{backgroundColor: '#dc3545'}}>✓</button>
                          <button onClick={cancelEdit} className="px-2 py-1 bg-gray-500 rounded text-xs font-bold">✗</button>
                        </div>
                      ) : (
                        <>
                          <div className="font-medium flex-1 text-sm">
                            {Array.isArray(formatItemText(item.text)) ? (
                              formatItemText(item.text).map((line, idx) => (
                                <div key={idx}>{line}</div>
                              ))
                            ) : (
                              formatItemText(item.text)
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-bold px-2 py-1 rounded text-xs" style={{backgroundColor: '#dc3545'}}>{item.rating}</span>
                            <button onClick={() => startEdit(item, 'red')} className="p-1 hover:bg-red-700 rounded" title="Szerkesztés">
                              <Edit size={12} />
                            </button>
                            <button onClick={() => deleteItem(item.id, 'red')} className="p-1 hover:bg-red-800 rounded" title="Törlés">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {drainItems.length === 0 && (
                    <p className="text-gray-500 italic text-center py-3 text-sm">Még nincsenek energia faló elemek</p>
                  )}
                </div>
              </div>
              
              {/* Energizáló elemek */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  {energyItems.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-2 rounded border" style={{backgroundColor: lightAccentColor, color: 'white'}}>
                      {editingItem && editingItem.id === item.id ? (
                        <div className="flex-1 flex gap-2 items-center">
                          <input
                            type="text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="flex-1 px-2 py-1 rounded text-gray-800 text-sm"
                            onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                          />
                          <button onClick={saveEdit} className="px-2 py-1 rounded text-xs font-bold" style={{backgroundColor: accentColor}}>✓</button>
                          <button onClick={cancelEdit} className="px-2 py-1 bg-gray-500 rounded text-xs font-bold">✗</button>
                        </div>
                      ) : (
                        <>
                          <div className="font-medium flex-1 text-sm">
                            {Array.isArray(formatItemText(item.text)) ? (
                              formatItemText(item.text).map((line, idx) => (
                                <div key={idx}>{line}</div>
                              ))
                            ) : (
                              formatItemText(item.text)
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="font-bold px-2 py-1 rounded text-xs" style={{backgroundColor: accentColor}}>{item.rating}</span>
                            <button onClick={() => startEdit(item, 'green')} className="p-1 hover:bg-green-600 rounded" title="Szerkesztés">
                              <Edit size={12} />
                            </button>
                            <button onClick={() => deleteItem(item.id, 'green')} className="p-1 hover:bg-red-600 rounded" title="Törlés">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {energyItems.length === 0 && (
                    <p className="text-gray-500 italic text-center py-3 text-sm">Még nincsenek energizáló elemek</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Számítási eredmények */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8 max-w-2xl mx-auto">
          <div className="w-full flex justify-center gap-4 mt-8">
            <button
              onClick={calculate}
              className="text-white font-bold py-2 px-6 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
              style={{backgroundColor: accentColor}}
            >
              Számítás
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 text-white font-bold py-2 px-6 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg bg-gray-600 hover:bg-gray-700"
            >
              <FileText size={16} />
              Mentés PDF-be
            </button>
          </div>
          
          {calculated && (
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              <div className="text-center p-4 rounded-lg border" style={{backgroundColor: lightAccentColor, color: 'white'}}>
                <h4 className="text-lg font-semibold mb-2">Energizáló</h4>
                <p className="text-3xl font-bold">{totalEnergy}</p>
              </div>
              <div className="text-center p-4 rounded-lg border" style={{backgroundColor: '#ff6b6b', color: 'white'}}>
                <h4 className="text-lg font-semibold mb-2">Energia faló</h4>
                <p className="text-3xl font-bold">{totalDrain}</p>
              </div>
              <div className="text-center p-4 rounded-lg border-2" style={{
                backgroundColor: totalEnergy - totalDrain > 0 ? lightAccentColor : totalEnergy - totalDrain < 0 ? '#ff6b6b' : '#cccccc',
                color: 'white'
              }}>
                <h4 className="text-lg font-semibold mb-2">
                  Nettó Energia
                </h4>
                <p className="text-3xl font-bold">
                  {totalEnergy - totalDrain > 0 ? '+' : ''}{totalEnergy - totalDrain}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Értékelési modal */}
      {showRating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4" style={{color: '#212529'}}>
              Értékeld: <span style={{color: accentColor}}>{currentItem}</span>
            </h3>
            <div className="mb-6">
              <label className="block text-xs mb-1 font-bold text-gray-800">
                {selectedType === 'green' ? 'Mennyire erős ez az energia forrás?' : 'Mennyire intenzív ez a lemerités?'} (1-10)
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={currentRating}
                onChange={(e) => setCurrentRating(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${(currentRating-1)*11.11}%, #ccc ${(currentRating-1)*11.11}%, #ccc 100%)`
                }}
              />
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>1</span>
                <span className="font-bold text-lg" style={{color: '#212529'}}>{currentRating}</span>
                <span>10</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRating(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Mégse
              </button>
              <button
                onClick={submitRating}
                className="flex-1 px-4 py-2 text-white rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
                style={{backgroundColor: accentColor}}
              >
                Hozzáad
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergiaTartaly;